import { ArtTile, formatClock } from '../index.js';

import type { StyleWithVars } from '../index.js';
import type { PartyWrapped, WrappedEdgeTrack } from '@mixwire/contracts';
import type { ReactElement } from 'react';

type Slide11BookendsProps = {
  readonly wrapped: PartyWrapped;
};

type EndProps = {
  readonly label: string;
  readonly track: WrappedEdgeTrack;
  readonly channels: PartyWrapped['channels'];
  readonly delayMs: number;
};

const colourOf = (channels: PartyWrapped['channels'], slotNumber: number): string =>
  channels.find((candidate) => candidate.slotNumber === slotNumber)?.colour ?? 'currentcolor';

const End = ({ label, track, channels, delayMs }: EndProps): ReactElement => {
  const style: StyleWithVars = { '--d': String(delayMs) };

  // The channel is shown as its colour alone. The slot's own display name is
  // the name of whoever holds it, so printing it here would name the DJ twice,
  // and no colour word can be derived honestly from an arbitrary hex — the
  // slot number goes to the screen reader instead.
  const dotStyle: StyleWithVars = { '--c': colourOf(channels, track.slotNumber) };

  return (
    <div className="end flip" style={style}>
      <p className="small">{label}</p>
      <ArtTile album={track.album} artUrl={track.artUrl} label={track.name} />
      <b>{track.name}</b>
      <p className="end-artists">{track.artists.join(', ')}</p>
      <p className="end-when">
        <span className="end-time">{formatClock(new Date(track.at))},</span>{' '}
        <span className="end-dj">{track.dj}</span>
        <i
          className="end-slot"
          style={dotStyle}
          role="img"
          aria-label={`channel ${String(track.slotNumber)}`}
        />
      </p>
    </div>
  );
};

export const Slide11Bookends = ({ wrapped }: Slide11BookendsProps): ReactElement => {
  const { opener, closer, channels } = wrapped;
  const isPair = opener !== null && closer !== null;

  return (
    <div className={isPair ? 'bookends' : 'bookends is-single'}>
      {opener === null ? null : (
        <End label="It opened with" track={opener} channels={channels} delayMs={200} />
      )}
      {closer === null ? null : (
        <End
          label={opener === null ? 'It closed with' : 'and closed with'}
          track={closer}
          channels={channels}
          delayMs={isPair ? 1000 : 200}
        />
      )}
    </div>
  );
};
